import {
	configFromEnv,
	type IcClientConfig,
} from "@motokomaker/ic-client";

/** Mainnet Internet Identity authorize URL (passkeys preferred). */
export const MAINNET_II_URL = "https://id.ai/authorize";

export function getEnvMap(): Record<string, string | undefined> {
	return import.meta.env as Record<string, string | undefined>;
}

export function getIcConfig(): IcClientConfig {
	const env = getEnvMap();
	return configFromEnv({
		...env,
		VITE_CANISTER_ID_PROTOCOL:
			env.VITE_CANISTER_ID_PROTOCOL ?? env.VITE_PROTOCOL_CANISTER_ID,
	});
}

export function hasProtocolEnv(): boolean {
	const env = getEnvMap();
	return Boolean(
		env.VITE_CANISTER_ID_PROTOCOL || env.VITE_PROTOCOL_CANISTER_ID,
	);
}

export function isLocalNetwork(config?: IcClientConfig): boolean {
	const c = config ?? getIcConfig();
	return c.network === "local";
}

export function networkLabel(
	config?: IcClientConfig,
): IcClientConfig["network"] {
	return (config ?? getIcConfig()).network;
}

export function resolveIdentityProvider(config?: IcClientConfig): string {
	const c = config ?? getIcConfig();
	const env = getEnvMap();
	if (env.VITE_II_URL) {
		const raw = String(env.VITE_II_URL).trim();
		try {
			const u = new URL(raw);
			if (u.hostname === "id.ai") return MAINNET_II_URL;
		} catch {
			/* fall through */
		}
		return raw || MAINNET_II_URL;
	}
	return c.identityProvider || MAINNET_II_URL;
}

/**
 * Local SPA: use page origin on {assets}.localhost (official local SPA URL).
 * Agent API host remains VITE_IC_HOST (127.0.0.1) — separate from SPA origin.
 */
export function resolveDerivationOrigin(config?: IcClientConfig): string | undefined {
	const c = config ?? getIcConfig();
	const env = getEnvMap();
	if (env.VITE_DERIVATION_ORIGIN?.trim()) {
		return env.VITE_DERIVATION_ORIGIN.trim();
	}
	if (c.network === "local" && typeof window !== "undefined") {
		const h = window.location.hostname.toLowerCase();
		if (h.endsWith(".localhost") && h !== "localhost") {
			return window.location.origin;
		}
		try {
			const host = c.host || "http://127.0.0.1:8000";
			const u = new URL(host);
			return `http://127.0.0.1:${u.port || "8000"}`;
		} catch {
			return "http://127.0.0.1:8000";
		}
	}
	if (typeof window !== "undefined") return window.location.origin;
	return c.derivationOrigin;
}
